import { api } from "@/lib/api";

interface ScrapeResponse {
  task_id: string;
}

export interface AdLibraryResponse {
  status: string;
  results: FacebookAd[];
  overview?: string;
  created_at?: string;
  strategy_evolution?: string;
  structured_data?: Record<string, unknown>;
}

interface ScrapeStatus {
  status: "in_progress" | "completed" | "failed";
  progress: number;
  results: FacebookAd[] | null;
  error: string | null;
}

export interface AdSnapshotCard {
  title: string | null;
  body_text: string | null;
  caption: string | null;
  cta_text: string | null;
  cta_type: string | null;
  original_image_url: string | null;
  resized_image_url: string | null;
  video_hd_url: string | null;
  video_preview_image_url: string | null;
  video_sd_url: string | null;
}

export interface AdSnapshot {
  title: string | null;
  body_text: string | null;
  caption: string | null;
  cta_text: string | null;
  cta_type: string | null;
  display_format: string | null;
  display_format_display: string | null;
  link_url: string | null;
  original_image_url: string | null;
  resized_image_url: string | null;
  video_hd_url: string | null;
  video_preview_image_url: string | null;
  cards: AdSnapshotCard[];
}

export interface AdAnalysis {
  overview: string;
  metadata: Record<string, unknown>;
}

export interface Brand {
  id: number;
  name: string;
  facebook_page_id: string;
  facebook_page_url: string;
  industry: string;
  about: string;
  profile_photo: string;
}

export interface FacebookAd {
  id: number;
  external_ad_id: string;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  publisher_platforms: string[];
  brand: Brand;
  latest_snapshot: AdSnapshot | null;
  latest_analysis: AdAnalysis | null;
}

export const useStartScrape = () => {
  return api.usePost<ScrapeResponse, { facebook_url: string }>("/api/scrape/");
};

export const useScrapeStatus = (taskId: string | null) => {
  return api.useGet<ScrapeStatus>(`/api/scrape/${taskId}/`, {
    enabled: !!taskId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data || data.status === "in_progress") {
        return 1000; // Poll every second while in progress
      }
      return false; // Stop polling when complete or failed
    },
  });
};

export const useGetAdsByPageId = (pageId: string | null) => {
  return api.useGet<AdLibraryResponse>("/api/ads/", {
    params: { page_id: pageId || "" },
    enabled: !!pageId,
  });
};

export function useAvailableBrands() {
  return api.useGet<Brand[]>("/api/brands/");
}
